import { useState } from 'react';
import { Button, Dialog, Select } from '@nyce/admin-kit/ui';
import { ConfirmDialog } from '../../../pages/databases/shared';
import { monitorsFor, type BuddySystemRef, type Monitor, type MonitorTestResult } from '../../api/monitors';
import { systemsFor } from './form';
import { runSummary } from './parts';
import { TestResultView } from './TestResultView';

type Action = 'run' | 'test';

/**
 * Test (rows per source, nothing alerted) and run now (a real run: alerts, emails) of a saved monitor. A run always
 * asks first; a monitor that may run on several systems asks which one.
 */
export function useRunTest(
  target: string,
  systems: BuddySystemRef[],
  notify: { success: (s: string) => void; error: (s: string) => void },
  after?: () => void
) {
  const [pick, setPick] = useState<{ monitor: Monitor; action: Action; systemId: string } | null>(null);
  const [test, setTest] = useState<{ monitor: Monitor; result: MonitorTestResult } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const api = monitorsFor(target);

  const runTest = async (m: Monitor, systemId: string) => {
    setBusy(m.id);
    try {
      setTest({ monitor: m, result: await api.test(m.id, systemId) });
    } catch (e) {
      notify.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  /** `systemId`: a known one (an assignment row); otherwise the monitor's own, or the only candidate, or asked. */
  const start = (m: Monitor, action: Action, systemId?: string) => {
    const candidates = systemsFor(m, systems);
    const sid = systemId ?? m.systemId ?? (candidates.length === 1 ? candidates[0].id : undefined);
    if (action === 'test' && sid) return void runTest(m, sid);
    setPick({ monitor: m, action, systemId: sid ?? '' });
  };

  const element = (
    <>
      {pick?.action === 'run' && (
        <ConfirmDialog
          title={`Run ${pick.monitor.name} now`}
          confirmLabel="Run now"
          canConfirm={!!pick.systemId}
          onClose={() => setPick(null)}
          onConfirm={async () => {
            const r = await api.run(pick.monitor.id, pick.systemId);
            setPick(null);
            notify.success(
              `Run on ${r.systemId}: ${r.status}, ${runSummary(r)}${r.error ? `: ${r.error}` : ''}`
            );
            after?.();
          }}
        >
          <p>
            A real run, exactly like a scheduled one: new alerts open, the investigation agent spends tokens
            and notifications are emailed. It cannot be undone.
          </p>
          <SystemPick
            monitor={pick.monitor}
            systems={systems}
            value={pick.systemId}
            onChange={(systemId) => setPick({ ...pick, systemId })}
          />
        </ConfirmDialog>
      )}
      {pick?.action === 'test' && (
        <Dialog
          open
          onClose={() => setPick(null)}
          title={`Test ${pick.monitor.name} on…`}
          maxWidth={460}
          footer={
            <div className="bd-toolbar">
              <Button variant="ghost" onClick={() => setPick(null)}>
                Cancel
              </Button>
              <Button
                disabled={!pick.systemId}
                onClick={() => {
                  setPick(null);
                  void runTest(pick.monitor, pick.systemId);
                }}
              >
                Test
              </Button>
            </div>
          }
        >
          <SystemPick
            monitor={pick.monitor}
            systems={systems}
            value={pick.systemId}
            onChange={(systemId) => setPick({ ...pick, systemId })}
          />
        </Dialog>
      )}
      {test && (
        <Dialog open onClose={() => setTest(null)} title={`Test of ${test.monitor.name}`} maxWidth={880}>
          <TestResultView result={test.result} />
        </Dialog>
      )}
    </>
  );
  return { start, busy, element };
}

function SystemPick({
  monitor,
  systems,
  value,
  onChange,
}: {
  monitor: Monitor;
  systems: BuddySystemRef[];
  value: string;
  onChange: (v: string) => void;
}) {
  const options = systemsFor(monitor, systems).map((s) => ({ value: s.id, label: `${s.name} (${s.id})` }));
  // The monitor's own system may be missing from the lookup (or the lookup failed): still offer it.
  if (value && !options.some((o) => o.value === value)) options.unshift({ value, label: value });
  return (
    <Select label="System" value={value} onChange={onChange} placeholder="Select system…" options={options} />
  );
}
